import * as Sentry from '@sentry/angular';
import { BrowserOptions } from '@sentry/browser';

import { environment } from '@app/environments/environment';

const SENTRY_DNS = 'https://5367df9ab787666e4fddd2b7a22a2e5a@o4507842986704896.ingest.de.sentry.io/4507849012084816';

interface ThirdPartyErrorRule {
  message: string | RegExp;
  scripts: string[];
}

/**
 * Rules for filtering out errors caused by third-party scripts (loaded via GTM, etc.).
 * Each rule matches a specific error message pattern AND verifies the stacktrace
 * contains frames from the specified third-party scripts.
 *
 * To add a new rule:
 *  1. Find the error message in Sentry
 *  2. Identify the third-party script(s) in the stacktrace
 *  3. Add a new entry to this array
 */
const THIRD_PARTY_ERROR_RULES: ThirdPartyErrorRule[] = [
  {
    message: "Failed to read a named property 'removeEventListener' from 'Window'",
    scripts: ['rrweb', 'posthog'],
  },
  {
    message: /Blocked a frame with origin .* from accessing a cross-origin frame/,
    scripts: ['/ekr/snippet'],
  },
  // Nebular bug: NbLayoutComponent.blockScroll() accesses layoutContainerRef.nativeElement before
  // the ViewChild is initialized when a dialog opens before NbLayout finishes rendering.
  // Non-fatal — dialog opens correctly afterward. Unfixed upstream: https://github.com/akveo/nebular/issues/2513
  {
    message: /Cannot read properties of undefined \(reading 'nativeElement'\)/,
    scripts: ['@nebular/theme'],
  },
  // Userflow SDK loader error (adblock/network/CSP) is third-party and non-actionable in app code.
  {
    message: /Could not load Userflow\.js/,
    scripts: ['userflow.js/dist/userflow.es', 'userflow.js'],
  },
  // Ignore any errors originating from GTM / Google Analytics scripts.
  // These are third-party script failures and should not consume our Sentry error quota.
  {
    message: /.+/,
    scripts: ['/gtm.js', 'googletagmanager.com/gtm.js', 'googletagmanager.com/gtag/js', 'google-analytics.com'],
  },
];

function isThirdPartyError(event: Sentry.ErrorEvent): boolean {
  const message = event.exception?.values?.[0]?.value ?? '';
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];

  return THIRD_PARTY_ERROR_RULES.some(rule => {
    const messageMatch = rule.message instanceof RegExp ? rule.message.test(message) : message.includes(rule.message);

    if (!messageMatch) {
      return false;
    }

    return frames.some(frame => rule.scripts.some(script => frame.filename?.includes(script)));
  });
}

const sharedOptions: BrowserOptions = {
  environment: environment.environmentName,
  release: environment.appVersionNumber,
  dsn: ['localhost', '127.0.0.1'].indexOf(window.location.hostname) > -1 ? null : SENTRY_DNS,
  integrations: [
    Sentry.browserApiErrorsIntegration({
      XMLHttpRequest: false,
    }),
  ],
  beforeSend(event) {
    if (isThirdPartyError(event)) {
      return null;
    }
    return event;
  },
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    /ExpressionChangedAfterItHasBeenCheckedError/,
    /ChunkLoadError/,
    // Org chart PNG export: html-to-image reads cross-origin styles (e.g. Google Fonts), browser blocks cssRules access → SecurityError. Export still succeeds; do not report to Sentry.
    /Failed to read the 'cssRules' property from 'CSSStyleSheet'/,
  ],
};

export const initSentry = (): void => {
  Sentry.init(sharedOptions);
};
